import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  CircleDot,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Gem,
  Gift,
  Briefcase,
  User,
  Award,
  Scissors,
  Ruler
} from 'lucide-react';
import { toast } from 'sonner';

// Belt types
const BELT_TYPES = [
  { value: 'classica', label: 'Cintura classica', icon: CircleDot },
  { value: 'casual', label: 'Cintura casual', icon: CircleDot },
  { value: 'elegante', label: 'Cintura elegante/cerimonia', icon: Gem },
  { value: 'western', label: 'Cintura western/texana', icon: Star },
  { value: 'intrecciata', label: 'Cintura intrecciata', icon: CircleDot },
  { value: 'reversibile', label: 'Cintura reversibile', icon: CircleDot },
  { value: 'senza_fibbia', label: 'Cintura senza fibbia (a incastro)', icon: CircleDot },
  { value: 'militare', label: 'Cintura stile militare', icon: Shield },
  { value: 'sottile', label: 'Cintura sottile da donna', icon: Sparkles },
  { value: 'alta', label: 'Cintura alta/fascia', icon: CircleDot },
  { value: 'altro', label: 'Altro', icon: CircleDot },
];

// Leather/Material types
const MATERIALS = [
  { value: 'vacchetta', label: 'Pelle vacchetta', icon: CircleDot },
  { value: 'vitello', label: 'Pelle di vitello', icon: Star },
  { value: 'cuoio', label: 'Cuoio pieno fiore', icon: Gem },
  { value: 'nabuk', label: 'Nabuk', icon: Sparkles },
  { value: 'intrecciata', label: 'Pelle intrecciata', icon: CircleDot },
  { value: 'coccodrillo', label: 'Pelle di coccodrillo', icon: Gem },
  { value: 'struzzo', label: 'Pelle di struzzo', icon: Gem },
  { value: 'pitone', label: 'Pelle di pitone', icon: Gem },
  { value: 'saffiano', label: 'Pelle saffiano', icon: Briefcase },
  { value: 'vegetale', label: 'Pelle conciata al vegetale', icon: Heart },
  { value: 'vegan', label: 'Pelle vegana / Eco-leather', icon: Heart },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Width options
const WIDTHS = [
  { value: 'sottile', label: 'Sottile (2-2.5 cm)', description: 'Elegante, da donna' },
  { value: 'stretta', label: 'Stretta (3 cm)', description: 'Classica elegante' },
  { value: 'media', label: 'Media (3.5-4 cm)', description: 'Versatile, la più comune' },
  { value: 'larga', label: 'Larga (4-4.5 cm)', description: 'Casual, jeans' },
  { value: 'extra', label: 'Extra larga (5+ cm)', description: 'Western, statement' },
  { value: 'non_so', label: 'Non so, vorrei consiglio', description: '' },
];

// Buckle types
const BUCKLES = [
  { value: 'ardiglione', label: 'Fibbia ad ardiglione (classica)', icon: CircleDot },
  { value: 'placca', label: 'Fibbia a placca', icon: CircleDot },
  { value: 'automatica', label: 'Fibbia automatica (a scatto)', icon: Zap },
  { value: 'doppio_ardiglione', label: 'Doppio ardiglione', icon: CircleDot },
  { value: 'western', label: 'Fibbia western decorata', icon: Star },
  { value: 'minimal', label: 'Fibbia minimal/nascosta', icon: Sparkles },
  { value: 'senza', label: 'Senza fibbia (a incastro)', icon: CircleDot },
  { value: 'personalizzata', label: 'Fibbia personalizzata', icon: Gem },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Heart },
];

// Colors
const COLORS = [
  { value: 'nero', label: 'Nero', icon: CircleDot },
  { value: 'marrone_scuro', label: 'Marrone scuro', icon: CircleDot },
  { value: 'marrone', label: 'Marrone medio', icon: CircleDot },
  { value: 'cognac', label: 'Cognac / Cuoio', icon: Star },
  { value: 'miele', label: 'Miele / Naturale', icon: Sparkles },
  { value: 'bordeaux', label: 'Bordeaux', icon: Gem },
  { value: 'blu', label: 'Blu navy', icon: CircleDot },
  { value: 'verde', label: 'Verde', icon: Heart },
  { value: 'bianco', label: 'Bianco / Panna', icon: Sparkles },
  { value: 'bicolore', label: 'Bicolore (reversibile)', icon: Sparkles },
  { value: 'personalizzato', label: 'Colore personalizzato', icon: Sparkles },
];

// Personalization options
const PERSONALIZATIONS = [
  { value: 'iniziali', label: 'Iniziali incise', icon: User },
  { value: 'nome', label: 'Nome completo', icon: User },
  { value: 'logo', label: 'Logo aziendale', icon: Briefcase },
  { value: 'dedica', label: 'Dedica / Frase', icon: Heart },
  { value: 'data', label: 'Data speciale', icon: Gift },
  { value: 'cuciture', label: 'Cuciture a contrasto', icon: Scissors },
  { value: 'bordo', label: 'Bordo colorato', icon: Sparkles },
  { value: 'nessuna', label: 'Nessuna personalizzazione', icon: CircleDot },
];

// Occasion
const OCCASIONS = [
  { value: 'lavoro', label: 'Lavoro / Ufficio', icon: Briefcase },
  { value: 'casual', label: 'Casual / Quotidiano', icon: User },
  { value: 'elegante', label: 'Elegante / Serata', icon: Gem },
  { value: 'cerimonia', label: 'Cerimonia / Matrimonio', icon: Heart },
  { value: 'regalo_lui', label: 'Regalo per lui', icon: Gift },
  { value: 'regalo_lei', label: 'Regalo per lei', icon: Gift },
  { value: 'compleanno', label: 'Compleanno', icon: Gift },
  { value: 'anniversario', label: 'Anniversario', icon: Heart },
  { value: 'altro', label: 'Altro', icon: CircleDot },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '40-70', label: '€40 - €70', description: 'Cintura base' },
  { value: '70-120', label: '€70 - €120', description: 'Pelle pregiata' },
  { value: '120-180', label: '€120 - €180', description: 'Alta qualità' },
  { value: '180-280', label: '€180 - €280', description: 'Pelletteria di lusso' },
  { value: '280-400', label: '€280 - €400', description: 'Pelli esotiche' },
  { value: '400+', label: '€400+', description: 'Su misura esclusiva' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '3weeks', label: '3 Settimane', description: 'Standard' },
  { value: '1month', label: '1 Mese', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example belts
const BELT_EXAMPLES = [
  {
    idea: "Cintura elegante per il mio matrimonio",
    result: "Cintura in cuoio nero lucido, fibbia in argento brunito, cuciture a mano nascoste, larghezza 3cm",
    image: "https://images.unsplash.com/photo-1624222247344-550fb60583dc?w=400&h=300&fit=crop",
    category: "Cerimonia"
  },
  {
    idea: "Cintura casual per jeans con le mie iniziali",
    result: "Cintura in pelle invecchiata color cognac, fibbia ad ardiglione in ottone, iniziali incise sul passante",
    image: "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&h=300&fit=crop",
    category: "Casual"
  },
  {
    idea: "Cintura da lavoro versatile ed elegante",
    result: "Cintura reversibile nero/marrone in vitello, fibbia girevole satinata, larghezza 3.5cm",
    image: "https://images.unsplash.com/photo-1611937685025-8d1df67a80b6?w=400&h=300&fit=crop",
    category: "Business"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho regalato una cintura personalizzata a mio padre per i 60 anni. La qualità del cuoio è straordinaria, le iniziali incise sono un tocco di classe. È rimasto commosso!",
    author: "Marco T.",
    location: "Milano"
  },
  {
    text: "Cercavo una cintura elegante per il matrimonio di mio fratello. L'artigiano ha creato un pezzo unico che si abbinava perfettamente al mio abito. Servizio impeccabile.",
    author: "Alessandro B.",
    location: "Firenze"
  },
  {
    text: "Dopo anni di cinture industriali che si rovinavano, ho deciso di investire in una cintura artigianale. È già un anno che la uso ogni giorno e sembra nuova!",
    author: "Davide R.",
    location: "Roma"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Qual è la larghezza giusta per una cintura da uomo?",
    answer: "Per un look formale/business, 3-3.5cm è l'ideale. Per casual/jeans, 4cm è perfetta. Le cinture molto strette (2.5cm) o molto larghe (5cm+) sono più di nicchia. L'artigiano ti consiglierà in base al tuo stile."
  },
  {
    question: "Quanto dura una cintura artigianale in pelle?",
    answer: "Una cintura in cuoio di qualità, se curata, dura facilmente 10-20 anni. La pelle conciata al vegetale migliora con l'uso, sviluppando una patina unica. È un investimento che ripaga nel tempo."
  },
  {
    question: "Posso scegliere il tipo di fibbia?",
    answer: "Assolutamente sì! Puoi scegliere materiale (ottone, acciaio, argento), finitura (lucida, satinata, brunita) e stile. Molti artigiani offrono anche fibbie intercambiabili."
  },
  {
    question: "Come funziona la personalizzazione con iniziali?",
    answer: "Le iniziali possono essere incise a caldo sulla pelle, stampate a secco, o applicate in metallo. La posizione classica è sul passante o sulla punta interna della cintura."
  },
  {
    question: "Che misura devo indicare?",
    answer: "La misura della cintura corrisponde alla circonferenza vita dove la indossi. L'artigiano ti chiederà la tua taglia pantaloni o la misura esatta. In caso di dubbio, è meglio abbondare: la cintura può essere accorciata, non allungata!"
  }
];

export default function CintureRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    belt_type: '',
    material: '',
    width: '',
    buckle: '',
    color: '',
    personalization: '',
    occasion: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Cinture Su Misura | Pelletteria Artigianale | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `belt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi la cintura che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_belt_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const beltTypeLabel = BELT_TYPES.find(t => t.value === formData.belt_type)?.label || '';
      const materialLabel = MATERIALS.find(t => t.value === formData.material)?.label || '';
      const widthLabel = WIDTHS.find(t => t.value === formData.width)?.label || '';
      const buckleLabel = BUCKLES.find(t => t.value === formData.buckle)?.label || '';
      const colorLabel = COLORS.find(t => t.value === formData.color)?.label || '';
      const personalizationLabel = PERSONALIZATIONS.find(t => t.value === formData.personalization)?.label || '';
      const occasionLabel = OCCASIONS.find(p => p.value === formData.occasion)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      // Build formatted description with all form data
      let fullDescription = formData.description;
      
      const details = [];
      if (beltTypeLabel) details.push(`🎯 Tipo: ${beltTypeLabel}`);
      if (materialLabel) details.push(`🧵 Materiale: ${materialLabel}`);
      if (widthLabel) details.push(`📏 Larghezza: ${widthLabel}`);
      if (buckleLabel) details.push(`🔗 Fibbia: ${buckleLabel}`);
      if (colorLabel) details.push(`🎨 Colore: ${colorLabel}`);
      if (personalizationLabel) details.push(`✨ Personalizzazione: ${personalizationLabel}`);
      if (occasionLabel) details.push(`🎁 Occasione: ${occasionLabel}`);
      if (budgetLabel) details.push(`💰 Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️ Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${beltTypeLabel} - ${materialLabel}`.trim() || 'Cintura su misura',
        description: fullDescription,
        category: 'Cinture',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dai pellettieri.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('belt-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Cinture Su Misura | Pelletteria Artigianale Italiana | Rarifex</title>
        <meta name="description" content="Cinture su misura in pelle italiana realizzate da maestri pellettieri. Scegli materiale, fibbia, larghezza e personalizzazioni. Artigianato Made in Italy. Gratuito e senza impegno." />
        <meta name="keywords" content="cintura su misura, cintura pelle, cintura artigianale, pelletteria italiana, cintura personalizzata, cintura cuoio, cintura uomo, cintura donna" />
        <link rel="canonical" href="https://www.rarifex.com/cinture-su-misura" />
        <meta property="og:title" content="Cinture Su Misura | Pelletteria Artigianale Italiana | Rarifex" />
        <meta property="og:description" content="Cinture su misura in pelle italiana realizzate da maestri pellettieri. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/cinture-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-stone-50/50 via-white to-amber-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-stone-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-stone-100 text-stone-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <CircleDot className="w-4 h-4" />
                    Pelletteria Su Misura
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    La cintura perfetta{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-stone-600 to-amber-700">
                      cucita su di te
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Maestri pellettieri italiani creano la cintura dei tuoi sogni.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Cuoio pregiato, fibbie artigianali, personalizzazioni uniche.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artigiani verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-slate-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="belt-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Cintura elegante per matrimonio"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="belt-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi la cintura che desideri *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Stile, occasione, taglia pantaloni, colore preferito. Allega foto di riferimento se ne hai."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="belt-description-textarea"
                        />
                      </div>
                      
                      {/* Type & Material */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Tipo di cintura
                          </Label>
                          <select
                            value={formData.belt_type}
                            onChange={(e) => handleInputChange('belt_type', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="belt-type-select"
                          >
                            <option value="">Seleziona...</option>
                            {BELT_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Materiale
                          </Label>
                          <select
                            value={formData.material}
                            onChange={(e) => handleInputChange('material', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="belt-material-select"
                          >
                            <option value="">Seleziona...</option>
                            {MATERIALS.map(mat => (
                              <option key={mat.value} value={mat.value}>{mat.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Width & Buckle */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Larghezza</Label>
                          <select
                            value={formData.width}
                            onChange={(e) => handleInputChange('width', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="belt-width-select"
                          >
                            <option value="">Seleziona...</option>
                            {WIDTHS.map(w => (
                              <option key={w.value} value={w.value}>
                                {w.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Fibbia</Label>
                          <select
                            value={formData.buckle}
                            onChange={(e) => handleInputChange('buckle', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="belt-buckle-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUCKLES.map(b => (
                              <option key={b.value} value={b.value}>{b.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Color & Personalization */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Colore</Label>
                          <select
                            value={formData.color}
                            onChange={(e) => handleInputChange('color', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="belt-color-select"
                          >
                            <option value="">Seleziona...</option>
                            {COLORS.map(col => (
                              <option key={col.value} value={col.value}>{col.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Personalizzazione</Label>
                          <select
                            value={formData.personalization}
                            onChange={(e) => handleInputChange('personalization', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="belt-personalization-select"
                          >
                            <option value="">Seleziona...</option>
                            {PERSONALIZATIONS.map(pers => (
                              <option key={pers.value} value={pers.value}>{pers.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Occasion & Budget */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Occasione</Label>
                          <select
                            value={formData.occasion}
                            onChange={(e) => handleInputChange('occasion', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="belt-occasion-select"
                          >
                            <option value="">Seleziona...</option>
                            {OCCASIONS.map(occ => (
                              <option key={occ.value} value={occ.value}>{occ.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                            data-testid="belt-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Timeline */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Tempi di consegna</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-stone-500 focus:border-stone-500"
                          data-testid="belt-timeline-select"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label} - {opt.description}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Attachments */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Foto di riferimento o ispirazioni
                        </Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(idx)}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-stone-300 hover:bg-stone-50/50 transition-colors">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploadingFile}
                                data-testid="belt-file-upload"
                              />
                              {uploadingFile ? (
                                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                              ) : (
                                <Upload className="w-5 h-5 text-slate-400" />
                              )}
                              <span className="text-sm text-slate-500">
                                Carica foto o ispirazioni ({formData.attachments.length}/5)
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-stone-600 to-amber-700 hover:from-stone-700 hover:to-amber-800 text-white shadow-lg shadow-stone-200"
                        data-testid="belt-submit-button"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <CircleDot className="w-5 h-5 mr-2" />
                        )}
                        Ricevi proposte gratis
                      </Button>
                      
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: PROBLEMA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                Le cinture industriali si rovinano e non ti rappresentano
              </h2>
              
              <div className="space-y-4 text-lg text-slate-600 mb-8">
                <p className="flex items-center justify-center gap-3">
                  <span className="text-slate-400">✗</span>
                  Pelle sintetica che si screpola dopo pochi mesi
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-slate-400">✗</span>
                  Fibbie che si ossidano o si rompono
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-slate-400">✗</span>
                  Taglie standard che non calzano mai perfettamente
                </p>
              </div>
              
              <p className="text-xl text-slate-700 font-medium">
                Una cintura artigianale dura una vita e racconta chi sei.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3: SOLUZIONE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-stone-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Una cintura{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-stone-600 to-amber-700">
                  unica come te
                </span>
              </h2>
              
              <p className="text-xl text-slate-600 mb-12">
                Maestri pellettieri italiani creano la cintura perfetta per te.
                <br />
                <strong className="text-slate-800">Tu scegli tutto, loro cuciono a mano.</strong>
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-stone-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Descrivi la tua cintura</h3>
                  <p className="text-slate-600">Pelle, fibbia, larghezza, colore, personalizzazioni</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-amber-700" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Ricevi proposte</h3>
                  <p className="text-slate-600">Pellettieri esperti ti contattano con preventivi</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Scegli il tuo artigiano</h3>
                  <p className="text-slate-600">Nessun obbligo, decidi solo se ti convince</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: ESEMPI */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-4">
                Da idea a cintura perfetta
              </h2>
              <p className="text-xl text-slate-600 text-center mb-12">
                Ecco cosa hanno creato i nostri pellettieri
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {BELT_EXAMPLES.map((example, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <img 
                      src={example.image} 
                      alt={example.result}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-5">
                      <span className="text-xs font-medium text-stone-600 uppercase tracking-wider">
                        {example.category}
                      </span>
                      <p className="text-sm text-slate-500 mt-2 mb-1">
                        Richiesta: "{example.idea}"
                      </p>
                      <p className="font-semibold text-slate-800">
                        → {example.result}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button 
                  onClick={scrollToForm}
                  variant="outline"
                  className="border-stone-200 text-stone-700 hover:bg-stone-50"
                >
                  Vuoi qualcosa di simile? Descrivi la tua idea
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5: COME FUNZIONA */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-stone-50/50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                In 4 semplici passaggi
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-stone-600 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Descrivi la cintura ideale</h3>
                    <p className="text-slate-600">
                      Racconta cosa cerchi: pelle, larghezza, fibbia, colore, personalizzazioni. Indica la tua taglia pantaloni per la misura perfetta.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-stone-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Ricevi proposte dai pellettieri</h3>
                    <p className="text-slate-600">
                      Artigiani specializzati vedono la tua richiesta e ti inviano proposte con pelli, fibbie, preventivi e tempi di realizzazione.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-amber-600 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Scegli e personalizza</h3>
                    <p className="text-slate-600">
                      Confronta le proposte, chatta con gli artigiani, affina i dettagli fino a trovare la soluzione perfetta per te.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Ricevi la tua cintura</h3>
                    <p className="text-slate-600">
                      L'artigiano realizza la cintura e te la spedisce. Un pezzo unico, cucito a mano, che durerà una vita.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: PERCHÉ FUNZIONA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Perché scegliere una cintura artigianale
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-stone-50">
                  <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Dura una vita</h3>
                    <p className="text-sm text-slate-600">Il cuoio migliora con l'uso</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-amber-50">
                  <Ruler className="w-8 h-8 text-amber-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Misura perfetta</h3>
                    <p className="text-sm text-slate-600">Tagliata sulla tua taglia esatta</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-stone-50">
                  <Sparkles className="w-8 h-8 text-stone-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Unica al mondo</h3>
                    <p className="text-sm text-slate-600">Iniziali, fibbia, colori a scelta</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-green-50">
                  <Zap className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Zero rischio</h3>
                    <p className="text-sm text-slate-600">Nessun obbligo di acquisto</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-stone-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Cosa dicono i clienti
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {TESTIMONIALS.map((testimonial, idx) => (
                  <Card key={idx} className="p-6 bg-white">
                    <div className="flex gap-1 mb-4">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center font-semibold text-stone-700">
                        {testimonial.author[0]}
                      </div>
                      <div>
                        <p className="font-medium">{testimonial.author}</p>
                        <p className="text-slate-500 text-xs">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: FAQ */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Domande frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, idx) => (
                  <div 
                    key={idx}
                    className="border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-stone-50 transition-colors"
                      data-testid={`belt-faq-${idx}`}
                    >
                      <span className="font-medium text-slate-800">{faq.question}</span>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 9: CTA FINALE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-stone-100 to-stone-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <CircleDot className="w-16 h-16 text-stone-600 mx-auto mb-6" />
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Pronto per la tua cintura su misura?
              </h2>
              
              <p className="text-xl text-slate-600 mb-8">
                I maestri pellettieri italiani aspettano la tua richiesta.
              </p>
              
              <Button
                onClick={scrollToForm}
                size="lg"
                className="py-6 px-10 text-lg font-semibold bg-gradient-to-r from-stone-600 to-amber-700 hover:from-stone-700 hover:to-amber-800 text-white shadow-lg shadow-stone-200"
                data-testid="belt-cta-final-button"
              >
                <CircleDot className="w-5 h-5 mr-2" />
                Ricevi proposte gratis ora
              </Button>
            </div>
          </div>
        </section>

      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">
              Per inviare la tua richiesta e ricevere proposte dai pellettieri, devi prima accedere o creare un account.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAuthPrompt(false)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={() => navigate('/login?redirect=/cinture-su-misura')}
                className="flex-1 bg-stone-600 hover:bg-stone-700"
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
